<template>
	<AppCard class="card-margin d-n" :title="getString('dojo_terminals_card_title')" :button-text="getString('save')" @button-click="saveSettings">
		<template #body>
			<AppList v-if="!hasDojo" :items="dojoSetupFields">
				<template #input-slot="{item}">
					<div class="row centered space-between">
						<p>{{item.label}}</p>
						<AppTextInput
							:value="item.value || ''"
							:placeholder="exampleDojoApiKey"
							type="password"
							id="dojo-api-key-input"
							@changing-text="item.callback"
							@focusout-input="(e, value)=>{item.callback(value)}"
							@pressed-enter="connectDojo"
						/>
					</div>
				</template>
			</AppList>
			<AppList v-else :items="terminals">
				<template #dropdown-slot="{item}">
					<div class="row centered space-between pb-20">
						<p>{{getString('pos_name')}}</p>
						<AppTextInput
							:value="item.name"
							:placeholder="getString('pos_name')"
							@pressed-enter="item.callbackText"
							@focusout-input="(e, value)=>{item.callbackText(value)}"
						/>
					</div>
					<div class="row centered flex-end">
						<AppButton
							:text="getString('delete')"
							outline small
							@click="$emit('delete-terminal', item)"
						/>
					</div>
				</template>
				<template #new-terminal-slot="{item}">
					<div class="row centered space-between row-clickable new-terminal-row" @click="item.callback">
						<div class="new-terminal-title">
							<div class="icon-plus" v-html="getIcon('iconPlus')"></div>
							<p class="chevron-p">{{ item.id === 'new_terminal_id' ? getString('add_pos') : item.name }}</p>
						</div>
						<div class="chevron-container">
							<AppChevronIcon :direction="item.active ? 'up' : 'down'"/>
						</div>
					</div>
				</template>
				<template #disconnect-slot="{item}">
					<div class="row centered flex-end disconnect-row">
						<AppButton 
							:text="getString('disconnect')" 
							outline 
							small 
							@click="disconnectDojo" 
						/>
					</div>
				</template>
				<template #new-terminal-dropdown-slot="{item}">
					<AppList :items="newTerminalFields">
						<template #input-slot="{item}">
							<div class="row centered space-between">
								<p>{{item.label}}</p>
								<AppTextInput
									:value="item.value || ''"
									:placeholder="item.label"
									@changing-text="item.callback"
									@focusout-input="(e, value)=>{item.callback(value)}"
								/>
							</div>
						</template>
					</AppList>
				</template>
			</AppList>
		</template>
	</AppCard>
	<CheckoutInPlace :client-secret="clientSecret" @toggling-popup="togglingPopupSheet" @checkout-complete="paymentComplete" />
</template>

<script>
import AppList from "@/shared/components/AppList.vue";
import AppButton from "@/shared/components/AppButton.vue";
import AppChecklist from "@/shared/components/AppChecklist.vue";
import AppChevronIcon from "@/shared/components/AppChevronIcon.vue";
import AppSelect from "@/shared/components/AppSelect.vue";
import AppCard from "@/shared/components/AppCard.vue";
import AppTextInput from "@/shared/components/AppTextInput.vue";
import {useLanguageStore, useNotificationStore, useSvgStore} from "@/shared/stores";
import {useRoomsStore, useUserStore} from "@/console-new/stores";
import AppPopup from "@/shared/components/AppPopup.vue";
import AppLoader from "@/shared/components/AppLoader.vue";
import CheckoutInPlace from "@/console-new/components/shared/CheckoutInPlace.vue";
import API from "@/shared/helpers/API";

export default {
	setup() {
		let languageStore = useLanguageStore();
		let notificationStore = useNotificationStore();
		let userStore = useUserStore();
		let roomsStore = useRoomsStore();
		const svgStore = useSvgStore();
		return {getString: languageStore.getString, showNotification: notificationStore.showNotification, userStore, roomsStore, getIcon: svgStore.get};
	},
	name: "PosListDojo",
	emits: ["save-settings", "delete-terminal"],
	components: {CheckoutInPlace, AppLoader, AppPopup, AppTextInput, AppCard, AppSelect, AppChevronIcon, AppChecklist, AppButton, AppList},
	data(){
		return {
			activeTerminalId: null,
			newTerminalObj: {
				id: 'new_terminal_id',
				title: this.getString('new_terminal'),
				chevronItem: true,
				dropdownSlot: 'new-terminal-dropdown-slot',
			},
			newTerminalName: '',
			newTerminalCode: '',
			clientSecret: null,
			dojoApiKey: '',
		}
	},
	computed: {
		exampleDojoApiKey() {
			return this.getString(TEST ? 'dojo_api_key_test_example' : 'dojo_api_key_prod_example');
		},
		hasDojo() {
			return this.userStore.currBusiness && this.userStore.currBusiness.dojo_api_key;
		},
		dojoSetupFields() {
			return [
				{
					label: this.getString('dojo_api_key'),
					title: this.getString('dojo_api_key'),
					key: "api_key",
					customSlot: "input-slot",
					callback: (value) => { this.dojoApiKey = value; },
					value: this.dojoApiKey
				}
			];
		},
		newTerminalFields() {
			return [
				{
					label: this.getString('pos_name'),
					title: this.getString('pos_name'),
					key: "name",
					customSlot: "input-slot",
					callback: (value) => { this.newTerminalName = value; },
					value: this.newTerminalName
				},
				{
					label: this.getString('new_pos_code'),
					title: this.getString('new_pos_code'),
					key: "code",
					customSlot: "input-slot",
					callback: (value) => {
						this.newTerminalCode = value.replace(/\s+/g, '-');
					},
					value: this.newTerminalCode
				},
			];
		},
		terminals() {
			let terminals = this.userStore.dojoTerminals || [];
			let finalTerminals = [];
			
			// Add disconnect button if no terminals but has API key
			if (terminals.length === 0 && this.hasDojo) {
				finalTerminals.push({
					id: 'disconnect_dojo',
					title: this.getString('disconnect_dojo'),
					customSlot: 'disconnect-slot'
				});
			}
			
			terminals.forEach((terminal) => {
				finalTerminals.push({
					id: terminal.terminal_id,
					title: terminal.name,
					name: terminal.name,
					callback: value => {
						this.activeTerminalId = value ? terminal.terminal_id : null;
					},
					callbackText: value => {
						let currentTerminal = this.userStore.dojoTerminals.find(t => t.terminal_id === terminal.terminal_id);
						if(currentTerminal && value) {
							currentTerminal.name = value;
						}
					},
					chevronItem: true,
					dropdownSlot: 'dropdown-slot',
					active: this.activeTerminalId === terminal.terminal_id,
				});
			});
			finalTerminals.push(this.newTerminal);
			return finalTerminals;
		},
		newTerminal() {
			let returningTerminal = Object.assign({}, this.newTerminalObj);
			returningTerminal.name = this.newTerminalName;
			returningTerminal.code = this.newTerminalCode;
			returningTerminal.active = this.activeTerminalId === 'new_terminal_id';
			returningTerminal.customSlot = "new-terminal-slot";
			returningTerminal.callback = () => {
				this.activeTerminalId = this.activeTerminalId === 'new_terminal_id' ? null : 'new_terminal_id';
			};
			return returningTerminal;
		},
	},
	methods: {
		paymentComplete() {
			setTimeout(() => {
				this.userStore.refreshBusiness(this.userStore.currBusinessId);
			}, 2000);
			setTimeout(() => {
				this.clientSecret = null;
			}, 2500);
		},
		togglingPopupSheet(v) {
			if(!v) {
				this.clientSecret = null;
			}
		},
		disconnectDojo() {
			API.init().integrationDojoDisconnect(this.userStore.currBusinessId, (resp) => {
				if (resp?.result === 'OK') {
					this.showNotification('generic-success');
					// Refresh business data and clear terminals
					this.userStore.currBusiness.dojo_api_key = null;
				} else {
					this.showNotification('generic-fail');
				}
			});
		},
		connectDojo() {
			if (!this.dojoApiKey || this.dojoApiKey.trim() === '') {
				this.showNotification('no-key-inserted');
				return;
			}

			API.init().integrationDojoConnect(this.userStore.currBusinessId, this.dojoApiKey, (resp) => {
				if (resp?.result === 'OK') {
					this.showNotification('generic-success');
					this.userStore.currBusiness.dojo_api_key = this.dojoApiKey;

					if (resp.terminals && Array.isArray(resp.terminals)) {
						this.userStore.dojoTerminals = resp.terminals;
					}
					// Refresh business data
					this.userStore.refreshBusiness(this.userStore.currBusinessId);
					this.dojoApiKey = '';
				} else {
					let errorMessage = 'generic-fail';
					if(resp?.details === 'failed_to_fetch_terminals') {
						errorMessage = 'wrong-api-key';
					} else if(resp?.details === 'invalid_data') {
						errorMessage = 'no-key-inserted';
					}
					this.showNotification(errorMessage);
				}
			});
		},
		saveSettings() {
			// If no dojo API key set, connect with the provided key
			if (!this.hasDojo) {
				this.connectDojo();
				return;
			}

			let name = this.newTerminalName;
			let code = this.newTerminalCode;
			if((name || code) && (!name || !code)){
				this.showNotification('incomplete-new-terminal-warning');
				return;
			}
			if(code && code.length < 7) {
				this.showNotification('short-pos-code');
				return;
			}
			let newTerminalData = name && code ? {
				name: name,
				code: code,
				terminal_id: 'new_terminal'
			} : null;

			this.$emit('save-settings', newTerminalData, () => {
				this.activeTerminalId = null;
				this.newTerminalName = '';
				this.newTerminalCode = '';
			});

		},
	}
}
</script>

<style scoped lang="scss">
@use "@/console-new/assets/scss/_variables.scss" as *;

.card-margin {
	margin-top: var(--main-padding);
}
.card-container:deep(.card-body) {
	padding: 0 !important;
	& > .list-container {
		.list-item:first-child::before {
			display: none;
		}
		.list-item:last-child::after {
			display: none;
		}
	}
}
.extra-text {
	width: 100%;
	cursor: pointer;
	p {
		font-size: 14px;
		line-height: 16px;
		color: #9B9B9B;
	}
}
.switch-header-wrapper {
	@include centered;
	justify-content: flex-end;
	@media (max-width: 350px) {
		width: 100%;
		margin-top: 10px;
	}
}
.chevron-p {
	width: calc(100% - 75px);
}
.chevron-p, .no-printers-row p {
	@media (max-width: 600px) {
		font-size: 14px;
	}
	@media (max-width: 400px) {
		font-size: 12px;
	}
}
.no-printers-row {
	@include centered;
	justify-content: flex-start;
	padding: 10px;
}
.row-clickable {
	cursor: pointer;
	img {
		display: none;
	}
	.chevron-container {
		@include centered;
		justify-content: flex-end;
		width: 50px;
		height: 35px;
	}
}
.new-terminal-row {
	min-height: 35px;
	cursor: pointer;
	background-color: var(--color-bg-primary-2);
	padding: 5px 10px;
	border-radius: 13px;
	.chevron-container {
		@include centered;
		justify-content: flex-end;
		width: 50px;
		height: 35px;
	}
	.new-terminal-title {
		display: flex;
		align-items: center;
		width: calc(100% - 50px);
		p {
			color: var(--color-primary);
		}
		.icon-plus {
			width: 14px;
			height: 14px;
			margin-right: 10px;
			display: flex;
			align-items: center;
			justify-content: center;

			&:deep(svg) {
				width: 100%;
				height: 100%;

				.fill {
					fill: var(--color-primary);
				}
			}
		}
	}
}
.card-margin {
	margin-top: $mainPadding;
}
.disconnect-row {
	padding: 10px;
}
</style>